
import React from "react";
import { Project } from "@/types";
import { Button } from "@/components/ui/button";
import { 
  ArrowUpRight, 
  CheckCircle, 
  Clock, 
  Folder, 
  AlertTriangle,
  Ban
} from "lucide-react";

interface ProjectCardProps {
  project: Project;
  onSelect: (project: Project) => void;
}

export default function ProjectCard({ project, onSelect }: ProjectCardProps) {
  const getStatusIcon = (project: Project) => {
    const status = project.status || 'active';
    switch (status) {
      case 'active':
        return <CheckCircle className="h-5 w-5 text-green-500" />;
      case 'paused':
        return <Clock className="h-5 w-5 text-amber-500" />;
      case 'archived':
        return <Ban className="h-5 w-5 text-gray-500" />;
      default:
        return <CheckCircle className="h-5 w-5 text-green-500" />;
    }
  };
  
  const getRiskIndicator = (project: Project) => {
    const highRisks = project.risks?.filter((risk: any) => 
      risk.status === 'open' && (risk.impact * risk.probability >= 10)
    ).length || 0;
    
    if (highRisks > 2) {
      return <AlertTriangle className="h-5 w-5 text-red-500" />;
    } else if (highRisks > 0) {
      return <AlertTriangle className="h-5 w-5 text-amber-500" />;
    } else {
      return <CheckCircle className="h-5 w-5 text-green-500" />;
    }
  };
  
  const calculateProgress = (project: Project) => {
    const totalStages = 8; // RIBA stages 0-7
    const completedStages = project.stages?.filter((s: any) => s.status === 'completed').length || 0;
    const currentStageProgress = project.stages?.[project.currentStage]?.progress || 0;
    
    const completeWeight = completedStages / totalStages;
    const currentWeight = 1 / totalStages * (currentStageProgress / 100);
    
    return Math.round((completeWeight + currentWeight) * 100);
  };

  return (
    <div 
      className="border rounded-lg shadow-sm hover:shadow transition-shadow p-4 cursor-pointer"
      onClick={() => onSelect(project)}
    >
      <div className="flex items-start justify-between mb-2">
        <div className="flex-1">
          <h2 className="text-lg font-semibold truncate">{project.name}</h2>
          <div className="text-sm text-muted-foreground flex items-center gap-1">
            <span>{project.client}</span>
            {project.referenceCode && (
              <>
                <span className="mx-1">•</span>
                <span className="font-mono">{project.referenceCode}</span>
              </>
            )}
          </div>
        </div>
        <div className="flex items-center">
          {getStatusIcon(project)}
        </div>
      </div>
      
      <div className="flex items-center justify-between my-3">
        <div className="flex items-center gap-1">
          <Folder className="h-4 w-4 text-blue-500" />
          <span className="text-sm">Stage {project.currentStage}: {project.stages?.[project.currentStage]?.name || 'Strategic Definition'}</span>
        </div>
        
        <div className="flex items-center">
          {getRiskIndicator(project)}
        </div>
      </div>
      
      <div className="mt-3">
        <div className="flex justify-between text-sm mb-1">
          <span>Progress</span>
          <span>{calculateProgress(project)}%</span>
        </div>
        <div className="w-full bg-gray-100 rounded-full h-2">
          <div
            className="bg-blue-500 h-2 rounded-full"
            style={{ width: `${calculateProgress(project)}%` }}
          ></div>
        </div>
      </div>
      
      <div className="mt-4 flex justify-end">
        <Button variant="ghost" size="sm" className="text-blue-600">
          Open <ArrowUpRight className="ml-1 h-4 w-4" />
        </Button>
      </div>
    </div>
  );
}
